import {
  SOCIAL_TOKEN_PATTERN,
  type ApiErrorCode,
  type GameId,
  type PhoneStartResponse,
  type PhoneVerifyResponse,
  type PutSaveConflict,
  type SaveData,
  type SaveRecord,
  type SavesResponse,
  type CreateSocialObjectRequest,
  type CreateSocialObjectResponse,
  type CurrentViewerResponse,
  type FeaturesResponse,
  type GuestSessionRequest,
  type GuestSessionResponse,
  type InstallationPlatform,
  type ResolvedSocialObject,
  type StartSocialObjectResponse,
  type Viewer,
} from "@enjoyment/contracts";

export const INSTALLATION_ID_STORAGE_KEY = "enjoyment.installation.v1";
const INSTALLATION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type FetchLike = (input: string | URL | Request, init?: RequestInit) => Promise<Response>;

export interface PlatformClientOptions {
  apiBaseUrl: string;
  platform: InstallationPlatform;
  appVersion?: string;
  locale?: string;
  storage?: KeyValueStorage;
  fetch?: FetchLike;
  now?: () => number;
  /**
   * Web pages on enjoyment.app: send the `ej_device` cookie with session requests so every game subdomain of this
   * browser is one account (docs/design/phone-login-admin.md). Off in the app.
   */
  shareBrowserAccount?: boolean;
}

/** A phone-login step's answer: ok, or the API's error code (`NETWORK` when the server could not be reached). */
export type PhoneStepResult<T> = { ok: true; value: T } | { ok: false; code: ApiErrorCode | "NETWORK" };
export type PutSaveResult = { ok: true; save: SaveRecord } | { ok: false; conflict: SaveRecord } | { ok: false; conflict?: undefined };

function browserStorage(): KeyValueStorage | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

function newInstallationId(): string {
  const crypto = globalThis.crypto;
  if (crypto?.randomUUID) return crypto.randomUUID();
  if (!crypto?.getRandomValues) throw new Error("Secure random UUID generation is unavailable");
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Lazy, anonymous online identity. Local games never need this client.
 * The random installation UUID is persisted once the server has answered with a session (so a device that never
 * reached the API holds no ID); bearer tokens stay in memory only.
 * Network failure returns null so solo/offline play can continue unchanged.
 */
export class PlatformClient {
  private readonly baseUrl: string;
  private readonly storage: KeyValueStorage | undefined;
  private readonly fetcher: FetchLike;
  private readonly now: () => number;
  private installationId?: string;
  private session?: GuestSessionResponse;
  private sessionRequest?: Promise<GuestSessionResponse | null>;
  /** Bumped by logout/forget: a session request started before that may not cache its token afterwards. */
  private generation = 0;

  constructor(private readonly options: PlatformClientOptions) {
    const url = new URL(options.apiBaseUrl);
    if (url.protocol !== "https:" && url.protocol !== "http:") throw new Error("API URL must use HTTP or HTTPS");
    url.search = "";
    url.hash = "";
    this.baseUrl = url.href.replace(/\/$/, "");
    this.storage = options.storage ?? browserStorage();
    this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.now = options.now ?? Date.now;
  }

  async ensureGuestSession(force = false): Promise<GuestSessionResponse | null> {
    if (!force && this.session && Date.parse(this.session.expiresAt) > this.now() + 60_000) return this.session;
    if (this.sessionRequest) return this.sessionRequest;
    const request = this.createGuestSession();
    this.sessionRequest = request;
    try {
      return await request;
    } finally {
      if (this.sessionRequest === request) this.sessionRequest = undefined;
    }
  }

  private async createGuestSession(): Promise<GuestSessionResponse | null> {
    const generation = this.generation;
    try {
      const installationId = this.getInstallationId();
      const body: GuestSessionRequest = {
        installationId,
        platform: this.options.platform,
        ...(this.options.appVersion ? { appVersion: this.options.appVersion } : {}),
        ...(this.options.locale ? { locale: this.options.locale } : {}),
      };
      const response = await this.fetcher(`${this.baseUrl}/v1/sessions/guest`, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        cache: "no-store",
        ...this.credentials(),
        body: JSON.stringify(body),
      });
      if (!response.ok) return null;
      const value = await response.json() as GuestSessionResponse;
      if (!value.accessToken || !value.viewer?.publicId || !Number.isFinite(Date.parse(value.expiresAt))) return null;
      if (generation !== this.generation) return null;
      // Only now may a server account exist for this installation: keep its ID (the launcher's "Verilerimi sil"
      // goes to the server only for a device that has one; offline or with identity off nothing is written).
      this.persistInstallationId(installationId);
      this.session = value;
      return value;
    } catch {
      return null;
    }
  }

  async currentViewer(): Promise<Viewer | null> {
    let session = await this.ensureGuestSession();
    if (!session) return null;
    try {
      let response = await this.fetchMe(session.accessToken);
      if (response.status === 401) {
        session = await this.ensureGuestSession(true);
        if (!session) return null;
        response = await this.fetchMe(session.accessToken);
      }
      if (!response.ok) return null;
      const value = await response.json() as CurrentViewerResponse;
      return value.viewer ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Revoke the current token on the server and drop it from memory. True when nothing is left to revoke. When the
   * server did not answer, the token is kept, so a retry really signs out (and drops the browser's account link)
   * instead of reporting success with nothing sent.
   */
  async logout(): Promise<boolean> {
    // A session still being created gets its token first, so that token is revoked too, not cached after logout.
    if (this.sessionRequest) await this.sessionRequest;
    const session = this.session;
    this.session = undefined;
    this.generation += 1;
    if (!session) return true;
    let done = false;
    try {
      const response = await this.fetcher(`${this.baseUrl}/v1/sessions/logout`, {
        method: "POST",
        headers: { authorization: `Bearer ${session.accessToken}`, accept: "application/json" },
        cache: "no-store",
        ...this.credentials(),
      });
      done = response.ok || response.status === 401;
    } catch {
      done = false;
    }
    if (!done && !this.session) this.session = session;
    return done;
  }

  /**
   * Erase this player's server-side account (H10), then forget the installation locally so the next online
   * action starts a fresh guest. False when the server could not be reached; local data is kept then.
   */
  async deleteAccount(): Promise<boolean> {
    let session = await this.ensureGuestSession();
    if (!session) return false;
    try {
      let response = await this.fetchDelete(session.accessToken);
      if (response.status === 401) {
        session = await this.ensureGuestSession(true);
        if (!session) return false;
        response = await this.fetchDelete(session.accessToken);
      }
      if (!response.ok) return false;
      this.clearLocalInstallation();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Share link (Phase 1B): a challenge or a seeded level. Starts the guest session lazily (an online platform
   * action). Null when offline or refused; the game shares its plain level link then.
   */
  async createSocialObject(input: CreateSocialObjectRequest): Promise<CreateSocialObjectResponse | null> {
    const response = await this.authed("POST", "/v1/social-objects", input);
    if (response?.status !== 201) return null;
    try {
      const value = await response.json() as CreateSocialObjectResponse;
      return SOCIAL_TOKEN_PATTERN.test(value.token) ? value : null;
    } catch {
      return null;
    }
  }

  /** What a link opens; needs no session. Null for unknown, expired or revoked links and when offline. */
  async resolveSocialObject(token: string): Promise<ResolvedSocialObject | null> {
    if (!SOCIAL_TOKEN_PATTERN.test(token)) return null;
    try {
      const response = await this.fetcher(`${this.baseUrl}/v1/social-objects/${token}`, {
        method: "GET",
        headers: { accept: "application/json" },
        cache: "no-store",
      });
      return response.ok ? await response.json() as ResolvedSocialObject : null;
    } catch {
      return null;
    }
  }

  /** The player starts play from a link (attribution). Null when the link is gone or the server is unreachable. */
  async startSocialObject(token: string): Promise<StartSocialObjectResponse | null> {
    if (!SOCIAL_TOKEN_PATTERN.test(token)) return null;
    const response = await this.authed("POST", `/v1/social-objects/${token}/start`);
    if (!response?.ok) return null;
    try {
      return await response.json() as StartSocialObjectResponse;
    } catch {
      return null;
    }
  }

  /** Take back one of this player's links. */
  async revokeSocialObject(token: string): Promise<boolean> {
    if (!SOCIAL_TOKEN_PATTERN.test(token)) return false;
    const response = await this.authed("POST", `/v1/social-objects/${token}/revoke`);
    return response?.status === 204;
  }

  /**
   * What this API offers (`GET /v1/features`, no session, cacheable). An API that predates the route answers 404: all
   * false. Null when the server cannot be reached (ask again later). Asking creates no identity.
   */
  async features(): Promise<FeaturesResponse | null> {
    try {
      const response = await this.fetcher(`${this.baseUrl}/v1/features`, { method: "GET", headers: { accept: "application/json" } });
      if (response.status === 404) return { identity: false, saves: false, phoneLogin: false };
      if (!response.ok) return null;
      const value = (await response.json()) as Partial<Record<keyof FeaturesResponse, unknown>>;
      return { identity: value.identity === true, saves: value.saves === true, phoneLogin: value.phoneLogin === true };
    } catch {
      return null;
    }
  }

  /**
   * True only when this device keeps an installation ID, i.e. a server identity may exist for it (one is written only
   * once the server answered with a session). Pages use it to decide whether opening a session is needed at all.
   */
  hasStoredInstallation(): boolean {
    try {
      return INSTALLATION_ID.test(this.storage?.getItem(INSTALLATION_ID_STORAGE_KEY) ?? "");
    } catch {
      return false;
    }
  }

  /** Send a 4-digit login code to a Turkish mobile (H9: the UI asks for 13+ before this). */
  async startPhoneLogin(phone: string, lang: "tr" | "en" = "tr"): Promise<PhoneStepResult<PhoneStartResponse>> {
    return this.step<PhoneStartResponse>("/v1/auth/phone/start", { phone, lang });
  }

  /**
   * Check the code and tie the phone to this device's account. When the phone already had an account elsewhere
   * the device moves to it (`switchedAccount`): the caller merges local progress into that account's saves.
   */
  async verifyPhoneLogin(input: { phone: string; code: string; whatsappOptIn: boolean }): Promise<PhoneStepResult<PhoneVerifyResponse>> {
    const result = await this.step<PhoneVerifyResponse>("/v1/auth/phone/verify", { ...input, ageConfirmed: true });
    if (result.ok) this.session = result.value.session;
    return result;
  }

  /** The player's answer to "may we write to you on WhatsApp?" (phone accounts). */
  async setWhatsappConsent(granted: boolean): Promise<Viewer | null> {
    const response = await this.authed("POST", "/v1/me/contact-consent", { channel: "whatsapp", granted });
    if (!response?.ok) return null;
    try {
      return ((await response.json()) as CurrentViewerResponse).viewer ?? null;
    } catch {
      return null;
    }
  }

  /** Every cloud save of this player (guests too). Null when offline. */
  async getSaves(): Promise<SaveRecord[] | null> {
    const response = await this.authed("GET", "/v1/saves");
    if (!response?.ok) return null;
    try {
      return ((await response.json()) as SavesResponse).saves;
    } catch {
      return null;
    }
  }

  /** Write one game's save merged from `baseVersion` (0: none yet). A conflict returns the server's current save. */
  async putSave(gameId: GameId, data: SaveData, baseVersion: number): Promise<PutSaveResult> {
    const response = await this.authed("PUT", `/v1/saves/${gameId}`, { data, baseVersion });
    if (!response) return { ok: false };
    try {
      if (response.ok) return { ok: true, save: ((await response.json()) as { save: SaveRecord }).save };
      if (response.status === 409) return { ok: false, conflict: ((await response.json()) as PutSaveConflict).current };
    } catch {
      // fall through
    }
    return { ok: false };
  }

  private async step<T>(path: string, body: unknown): Promise<PhoneStepResult<T>> {
    const response = await this.authed("POST", path, body);
    if (!response) return { ok: false, code: "NETWORK" };
    try {
      if (response.ok) return { ok: true, value: (await response.json()) as T };
      const error = (await response.json()) as { error?: { code?: ApiErrorCode } };
      return { ok: false, code: error.error?.code ?? "INTERNAL_ERROR" };
    } catch {
      return { ok: false, code: "INTERNAL_ERROR" };
    }
  }

  private credentials(): { credentials?: RequestCredentials } {
    return this.options.shareBrowserAccount ? { credentials: "include" } : {};
  }

  /** A request with the guest's Bearer token, retried once with a fresh session after a 401. */
  private async authed(method: "GET" | "POST" | "PUT", path: string, body?: unknown): Promise<Response | null> {
    let session = await this.ensureGuestSession();
    if (!session) return null;
    const send = (token: string) =>
      this.fetcher(`${this.baseUrl}${path}`, {
        method,
        headers: { authorization: `Bearer ${token}`, accept: "application/json", ...(body === undefined ? {} : { "content-type": "application/json" }) },
        cache: "no-store",
        ...(path.startsWith("/v1/auth/phone/verify") ? this.credentials() : {}),
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    try {
      let response = await send(session.accessToken);
      if (response.status === 401) {
        session = await this.ensureGuestSession(true);
        if (!session) return null;
        response = await send(session.accessToken);
      }
      return response;
    } catch {
      return null;
    }
  }

  /** Forget this browser installation locally. It does not claim to erase server-side data (see deleteAccount). */
  clearLocalInstallation(): void {
    this.session = undefined;
    this.generation += 1;
    this.installationId = undefined;
    try {
      this.storage?.removeItem(INSTALLATION_ID_STORAGE_KEY);
    } catch {
      // Storage may be disabled by the browser; this client remains memory-only.
    }
  }

  private fetchDelete(token: string): Promise<Response> {
    return this.fetcher(`${this.baseUrl}/v1/me`, {
      method: "DELETE",
      headers: { authorization: `Bearer ${token}`, accept: "application/json" },
      cache: "no-store",
      ...this.credentials(),
    });
  }

  private fetchMe(token: string): Promise<Response> {
    return this.fetcher(`${this.baseUrl}/v1/me`, {
      method: "GET",
      headers: { authorization: `Bearer ${token}`, accept: "application/json" },
      cache: "no-store",
    });
  }

  private getInstallationId(): string {
    if (this.installationId) return this.installationId;
    try {
      const stored = this.storage?.getItem(INSTALLATION_ID_STORAGE_KEY);
      if (stored && INSTALLATION_ID.test(stored)) {
        this.installationId = stored;
        return stored;
      }
    } catch {
      // Fall through to a memory-only installation identity.
    }
    const id = newInstallationId();
    this.installationId = id;
    return id;
  }

  private persistInstallationId(id: string): void {
    try {
      if (this.storage?.getItem(INSTALLATION_ID_STORAGE_KEY) !== id) this.storage?.setItem(INSTALLATION_ID_STORAGE_KEY, id);
    } catch {
      // Storage may be unavailable; a later page load can start a new anonymous identity.
    }
  }
}
